import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Send,
  Bitcoin,
  TrendingUp,
  Clock,
  Activity,
  Sparkles,
  Percent,
  MapPin,
  User2,
  Phone,
  Mail,
  CalendarClock,
  Coins,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAgentAuth } from "../context/AuthContext";

const fmt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtCount = (n) => Number(n || 0).toLocaleString();

/* ---------- BD time helpers ---------- */
const bdNow = () => {
  // Convert browser-local now → BD (UTC+6) display string.
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  return new Date(utcMs + 6 * 60 * 60_000);
};
const fmtBdDate = (d) =>
  d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
const fmtBdTime = (d) =>
  d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

export default function AgentDashboard() {
  const { agent } = useAgentAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bdClock, setBdClock] = useState(bdNow());

  const load = async () => {
    try {
      const { data: d } = await api.get("/agent/dashboard");
      setData(d);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // BD live clock — refresh every second
  useEffect(() => {
    const t = setInterval(() => setBdClock(bdNow()), 1000);
    return () => clearInterval(t);
  }, []);

  if (loading || !data) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <div className="relative">
          <div className="h-12 w-12 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          <div className="absolute inset-0 h-12 w-12 border-2 border-amber-400/30 border-b-transparent rounded-full animate-spin" style={{ animationDirection: "reverse", animationDuration: "1.5s" }} />
        </div>
      </div>
    );
  }

  const s = data.stats;
  const a = data.agent || agent || {};

  return (
    <div data-testid="agent-dashboard-page" className="max-w-[1400px] mx-auto">
      {/* ========== HERO with agent identity + BD clock ========== */}
      <div
        className="relative overflow-hidden rounded-2xl border border-white/[0.06] mb-5"
        style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(8,12,22,0.85) 40%, rgba(245,158,11,0.08) 100%)" }}
      >
        <div aria-hidden className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-emerald-500/15 blur-[100px]" />
        <div aria-hidden className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-amber-500/10 blur-[100px]" />

        <div className="relative px-4 sm:px-6 py-5 sm:py-7">
          {/* Top row: Live Overview badge + BD clock (right) */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-2.5 py-1">
              <Sparkles size={11} /> Live Overview
            </span>

            {/* BD clock */}
            <div
              data-testid="agent-bd-clock"
              className="hidden sm:flex flex-col px-3.5 py-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06]"
            >
              <div className="text-[9px] uppercase tracking-[1.6px] text-emerald-300/85 font-bold flex items-center gap-1">
                <CalendarClock size={10} /> Bangladesh Time
              </div>
              <div className="font-mono font-extrabold text-emerald-200 text-[14px] leading-tight tabular-nums mt-0.5">
                {fmtBdTime(bdClock)} <span className="text-white/40 font-normal text-[11px]">· {fmtBdDate(bdClock)}</span>
              </div>
            </div>
          </div>

          {/* BD clock — mobile only */}
          <div className="sm:hidden mb-4 px-3 py-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] flex items-center gap-2">
            <CalendarClock size={12} className="text-emerald-300/85" />
            <div className="text-[10px] uppercase tracking-[1.5px] text-emerald-300/85 font-bold">BD Time</div>
            <div className="font-mono font-extrabold text-emerald-200 text-[12px] tabular-nums ml-auto">
              {fmtBdTime(bdClock)} · {fmtBdDate(bdClock)}
            </div>
          </div>

          {/* Agent profile card */}
          <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-3">
            {/* identity + commissions */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
              <div className="flex items-center gap-3">
                <div
                  className="h-12 w-12 rounded-xl grid place-items-center font-black text-[15px] flex-shrink-0"
                  style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208", boxShadow: "0 10px 22px -10px rgba(245,158,11,0.6)" }}
                >
                  {(a?.name || a?.username || "A").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[14.5px] text-white truncate" data-testid="agent-hero-name">{a?.name || a?.username}</div>
                  <div className="text-[11px] text-white/55 font-mono truncate">@{a?.username}</div>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {(a?.status || "active").toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <CommissionPill
                  label="Deposit Commission"
                  value={`${a?.deposit_commission_pct || 0}%`}
                  Icon={ArrowDownToLine}
                  tone="emerald"
                  testid="agent-deposit-commission-pct"
                />
                <CommissionPill
                  label="Withdraw Commission"
                  value={`${a?.withdraw_commission_pct || 0}%`}
                  Icon={ArrowUpFromLine}
                  tone="amber"
                  testid="agent-withdraw-commission-pct"
                />
              </div>

              {/* Contact line */}
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-white/55">
                {a?.mobile && (
                  <span className="inline-flex items-center gap-1"><Phone size={10} className="text-emerald-300/70" /> {a.mobile}</span>
                )}
                {a?.email && (
                  <span className="inline-flex items-center gap-1 truncate"><Mail size={10} className="text-emerald-300/70" /> {a.email}</span>
                )}
              </div>
            </div>

            {/* Withdraw address card */}
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-7 w-7 rounded-lg grid place-items-center bg-amber-500/20 border border-amber-500/40">
                  <MapPin size={12} className="text-amber-300" />
                </div>
                <div className="text-[10px] uppercase tracking-[1.6px] font-bold text-amber-300">Withdraw Address</div>
              </div>
              <div
                className="text-[13px] font-bold text-white/90 leading-snug"
                data-testid="agent-withdraw-address"
              >
                {s.withdraw_address || <span className="text-white/40 font-medium">— not set —</span>}
              </div>
              <div className="text-[10.5px] text-white/40 mt-auto pt-2">
                Players will see this address when they request a withdrawal from you.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== TODAY's KPI grid ========== */}
      <SectionHeader Icon={Activity} label="Today's Activity" sub="All times in Bangladesh time (BDT)" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <TodayCard
          label="Total Deposit"
          amount={fmt(s.today_deposits)}
          countText={`${fmtCount(s.today_deposit_count)} ${s.today_deposit_count === 1 ? "deposit" : "deposits"}`}
          Icon={ArrowDownToLine}
          tone="emerald"
          testid="today-deposit"
        />
        <TodayCard
          label="Withdraw Received"
          amount={fmt(s.today_withdraw_received)}
          countText={`${fmtCount(s.today_withdraw_received_count)} ${s.today_withdraw_received_count === 1 ? "received" : "received"}`}
          Icon={CheckCircle2}
          tone="violet"
          testid="today-withdraw-received"
        />
        <TodayCard
          label="Withdraw Rejected"
          amount={fmt(s.today_withdraw_rejected)}
          countText={`${fmtCount(s.today_withdraw_rejected_count)} ${s.today_withdraw_rejected_count === 1 ? "rejected" : "rejected"}`}
          Icon={XCircle}
          tone="rose"
          testid="today-withdraw-rejected"
        />
        <TodayCard
          label="Today Commission"
          amount={fmt(s.today_commission)}
          countText={`Dep ${fmt(s.today_deposit_commission)} · Wd ${fmt(s.today_withdraw_commission)}`}
          Icon={Coins}
          tone="amber"
          testid="today-commission"
          mono
        />
      </div>

      {/* ========== Last 30 days stats grid ========== */}
      <SectionHeader Icon={TrendingUp} label="Overall Stats" sub="Last 30 days" />
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Total Balance"
          value={fmt(s.balance)}
          sub="Available now"
          icon={Wallet}
          tone="emerald"
          featured
          testid="agent-stat-balance"
        />
        <StatCard
          label="Total Deposits Sent"
          value={fmt(s.last30_deposits)}
          sub={`${fmtCount(s.last30_deposit_count)} txns · 30d`}
          icon={ArrowDownToLine}
          tone="sky"
          testid="agent-stat-deposits"
        />
        <StatCard
          label="Pending Withdrawals"
          value={fmtCount(s.pending_withdrawals)}
          sub="awaiting your code"
          icon={AlertTriangle}
          tone="amber"
          pulse={s.pending_withdrawals > 0}
          testid="agent-stat-pending"
        />
        <StatCard
          label="Approved Withdrawals"
          value={fmt(s.last30_approved_withdrawals)}
          sub={`${fmtCount(s.last30_approved_withdrawal_count)} txns · 30d`}
          icon={CheckCircle2}
          tone="violet"
          testid="agent-stat-approved"
        />
      </div>

      {/* ========== Quick actions + recent ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Quick actions */}
        <div
          className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-white/[0.08] p-4 sm:p-5"
          style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[14.5px] font-extrabold tracking-tight">Quick Actions</h3>
              <p className="text-[11px] text-white/45 mt-0.5">Jump to your most-used tools.</p>
            </div>
            <div className="h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 grid place-items-center">
              <Activity size={15} className="text-emerald-300" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ActionCard to="/agent/deposit" icon={Send} tone="emerald" title="Deposit to user" desc="Top up any player wallet" testid="agent-quick-deposit" />
            <ActionCard
              to="/agent/withdrawals"
              icon={ArrowUpFromLine}
              tone="amber"
              title="Approve withdrawals"
              desc="Verify with 6-digit code"
              testid="agent-quick-withdrawals"
              badge={s.pending_withdrawals > 0 ? s.pending_withdrawals : null}
            />
            <ActionCard to="/agent/usdt-deposit" icon={Bitcoin} tone="sky" title="USDT top-up" desc="Refill via crypto" testid="agent-quick-usdt" />
            <ActionCard to="/agent/history" icon={Clock} tone="violet" title="History log" desc="View all activity" testid="agent-quick-history" />
          </div>
        </div>

        {/* Recent activity — Deposits / Withdrawals tabs */}
        <div
          className="lg:col-span-3 relative overflow-hidden rounded-2xl border border-white/[0.08] p-4 sm:p-5"
          style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)" }}
        >
          <RecentActivity
            deposits={data.recent_deposits || []}
            withdrawals={data.recent_withdrawals || []}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Smaller building blocks                                      */
/* ============================================================ */

function SectionHeader({ Icon, label, sub }) {
  return (
    <div className="flex items-end justify-between gap-2 mb-2.5">
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-lg grid place-items-center bg-white/[0.04] border border-white/10">
          <Icon size={13} className="text-white/70" />
        </div>
        <div>
          <div className="text-[13px] font-extrabold tracking-tight">{label}</div>
          {sub && <div className="text-[10px] text-white/40">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function CommissionPill({ label, value, Icon, tone, testid }) {
  const tones = {
    emerald: { ring: "rgba(52,211,153,0.30)", text: "text-emerald-300", bg: "rgba(52,211,153,0.10)" },
    amber:   { ring: "rgba(251,191,36,0.30)", text: "text-amber-300",   bg: "rgba(251,191,36,0.10)" },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <div
      className="rounded-lg border px-3 py-2 flex items-center gap-2.5"
      style={{ background: t.bg, borderColor: t.ring }}
      data-testid={testid}
    >
      <div className="h-7 w-7 rounded-md grid place-items-center" style={{ background: "rgba(0,0,0,0.25)", border: `1px solid ${t.ring}` }}>
        <Icon size={12} className={t.text} />
      </div>
      <div className="min-w-0">
        <div className={`text-[9px] uppercase tracking-[1.4px] font-bold ${t.text}`}>{label}</div>
        <div className={`font-extrabold text-[14px] tabular-nums ${t.text} flex items-center gap-1`}>
          <Percent size={10} className="opacity-60" /> {value}
        </div>
      </div>
    </div>
  );
}

function TodayCard({ label, amount, countText, Icon, tone, testid }) {
  const tones = {
    emerald: { ring: "rgba(52,211,153,0.30)", text: "text-emerald-300", iconBg: "linear-gradient(135deg,#34d399,#059669)", iconColor: "#06140b", glow: "rgba(16,185,129,0.18)" },
    amber:   { ring: "rgba(251,191,36,0.30)", text: "text-amber-300",   iconBg: "linear-gradient(135deg,#fbbf24,#f59e0b)", iconColor: "#1a1208", glow: "rgba(245,158,11,0.20)" },
    violet:  { ring: "rgba(167,139,250,0.30)", text: "text-violet-300", iconBg: "linear-gradient(135deg,#a78bfa,#8b5cf6)", iconColor: "#11071f", glow: "rgba(167,139,250,0.20)" },
    rose:    { ring: "rgba(244,63,94,0.30)",  text: "text-rose-300",   iconBg: "linear-gradient(135deg,#fb7185,#f43f5e)", iconColor: "#1a0509", glow: "rgba(244,63,94,0.20)" },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-xl p-3.5 sm:p-4 border transition hover:-translate-y-0.5"
      style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.95) 0%, rgba(10,17,30,0.95) 100%)", borderColor: t.ring, boxShadow: `0 12px 28px -22px ${t.glow}` }}
    >
      <div aria-hidden className="absolute -top-12 -right-10 h-28 w-28 rounded-full blur-3xl opacity-50" style={{ background: t.glow }} />
      <div className="relative flex items-start justify-between gap-2 mb-2.5">
        <div className={`text-[9.5px] uppercase tracking-[1.6px] font-bold ${t.text}`}>{label}</div>
        <div className="h-8 w-8 rounded-lg grid place-items-center flex-shrink-0" style={{ background: t.iconBg, boxShadow: `0 8px 16px -8px ${t.glow}` }}>
          <Icon size={14} strokeWidth={2.5} style={{ color: t.iconColor }} />
        </div>
      </div>
      <div className="relative font-mono font-black tabular-nums text-[18px] sm:text-[22px] leading-tight tracking-tight">
        {amount}
      </div>
      <div className="relative text-[10.5px] text-white/45 mt-1.5 font-medium truncate">{countText}</div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, tone, testid, featured, pulse }) {
  const tones = {
    emerald: { ring: "rgba(52,211,153,0.30)", text: "text-emerald-300", iconBg: "rgba(52,211,153,0.15)", glow: "rgba(16,185,129,0.25)" },
    sky:     { ring: "rgba(56,189,248,0.30)", text: "text-sky-300",     iconBg: "rgba(56,189,248,0.15)", glow: "rgba(56,189,248,0.20)" },
    amber:   { ring: "rgba(251,191,36,0.30)", text: "text-amber-300",   iconBg: "rgba(251,191,36,0.15)", glow: "rgba(245,158,11,0.20)" },
    violet:  { ring: "rgba(167,139,250,0.30)", text: "text-violet-300", iconBg: "rgba(167,139,250,0.15)", glow: "rgba(167,139,250,0.20)" },
  };
  const t = tones[tone];
  return (
    <div
      data-testid={testid}
      className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 transition-all hover:-translate-y-0.5"
      style={{
        background: featured
          ? `linear-gradient(135deg, ${t.glow} 0%, rgba(8,12,22,0.95) 60%)`
          : "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)",
        border: `1px solid ${t.ring}`,
        boxShadow: featured ? `0 20px 50px -25px ${t.glow}` : "0 8px 24px -16px rgba(0,0,0,0.6)",
      }}
    >
      <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition-opacity" style={{ background: t.glow }} />
      <div className="relative flex items-start justify-between mb-3 sm:mb-4">
        <div className={`text-[10px] uppercase tracking-[1.6px] font-bold ${t.text}`}>{label}</div>
        <div className="relative h-9 w-9 rounded-xl grid place-items-center" style={{ background: t.iconBg, border: `1px solid ${t.ring}` }}>
          <Icon size={15} className={t.text} />
          {pulse && <span className="absolute inset-0 rounded-xl animate-ping" style={{ background: t.glow, opacity: 0.5 }} />}
        </div>
      </div>
      <div className="relative font-mono font-black tabular-nums tracking-tight text-[20px] sm:text-[26px] leading-none">{value}</div>
      {sub && <div className="relative text-[10.5px] sm:text-[11px] text-white/45 mt-1.5 sm:mt-2 font-medium truncate">{sub}</div>}
    </div>
  );
}

function ActionCard({ to, icon: Icon, tone, title, desc, testid, badge }) {
  const tones = {
    emerald: { bg: "rgba(52,211,153,0.10)", bgHover: "rgba(52,211,153,0.18)", border: "rgba(52,211,153,0.30)", text: "text-emerald-300", iconBg: "rgba(52,211,153,0.20)" },
    amber:   { bg: "rgba(251,191,36,0.10)", bgHover: "rgba(251,191,36,0.18)", border: "rgba(251,191,36,0.30)", text: "text-amber-300",   iconBg: "rgba(251,191,36,0.20)" },
    sky:     { bg: "rgba(56,189,248,0.10)", bgHover: "rgba(56,189,248,0.18)", border: "rgba(56,189,248,0.30)", text: "text-sky-300",     iconBg: "rgba(56,189,248,0.20)" },
    violet:  { bg: "rgba(167,139,250,0.10)", bgHover: "rgba(167,139,250,0.18)", border: "rgba(167,139,250,0.30)", text: "text-violet-300", iconBg: "rgba(167,139,250,0.20)" },
  };
  const t = tones[tone];
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative block rounded-xl p-3 sm:p-3.5 border transition-all hover:scale-[1.02] active:scale-[0.99]"
      style={{ background: t.bg, borderColor: t.border }}
      onMouseEnter={(e) => (e.currentTarget.style.background = t.bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = t.bg)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg grid place-items-center" style={{ background: t.iconBg }}>
          <Icon size={15} className={t.text} strokeWidth={2.4} />
        </div>
        {badge != null && (
          <span className="h-5 min-w-[20px] px-1.5 rounded-full grid place-items-center text-[10px] font-black bg-rose-500 text-white animate-pulse">
            {badge}
          </span>
        )}
      </div>
      <div className={`text-[12px] sm:text-[12.5px] font-extrabold tracking-tight ${t.text}`}>{title}</div>
      <div className="text-[10px] sm:text-[10.5px] text-white/45 mt-0.5">{desc}</div>
    </Link>
  );
}


/* ============================================================ */
/*  Recent Activity — tabbed deposits + withdrawals             */
/* ============================================================ */
function RecentActivity({ deposits, withdrawals }) {
  const [tab, setTab] = useState("deposits");
  const items = tab === "deposits" ? deposits : withdrawals;

  return (
    <>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-[14.5px] font-extrabold tracking-tight">
            {tab === "deposits" ? "Recent Deposits" : "Recent Withdrawals"}
          </h3>
          <p className="text-[11px] text-white/45 mt-0.5">
            {tab === "deposits" ? "Your last 5 successful transfers." : "Your last 5 withdrawal requests."}
          </p>
        </div>
        <Link
          to="/agent/history"
          className="text-[11.5px] text-emerald-300 hover:text-emerald-200 font-bold inline-flex items-center gap-1"
        >
          View all <TrendingUp size={11} />
        </Link>
      </div>

      {/* Tab pills */}
      <div className="inline-flex p-1 bg-white/[0.03] border border-white/[0.06] rounded-xl mb-3" data-testid="recent-activity-tabs">
        <RecentTabBtn
          active={tab === "deposits"}
          onClick={() => setTab("deposits")}
          Icon={ArrowDownToLine}
          tone="emerald"
          count={deposits.length}
          testid="recent-tab-deposits"
        >
          Deposits
        </RecentTabBtn>
        <RecentTabBtn
          active={tab === "withdrawals"}
          onClick={() => setTab("withdrawals")}
          Icon={ArrowUpFromLine}
          tone="violet"
          count={withdrawals.length}
          testid="recent-tab-withdrawals"
        >
          Withdrawals
        </RecentTabBtn>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          <div className="h-14 w-14 rounded-2xl bg-white/[0.03] mx-auto grid place-items-center mb-3 border border-white/5">
            {tab === "deposits" ? <Send className="text-white/30" size={22} /> : <ArrowUpFromLine className="text-white/30" size={22} />}
          </div>
          <div className="text-[13px] font-bold text-white/65">
            No {tab === "deposits" ? "deposits" : "withdrawals"} yet
          </div>
          <div className="text-[11.5px] mt-1">
            {tab === "deposits" ? (
              <>Head to <Link to="/agent/deposit" className="text-emerald-300 hover:underline">Deposit to user</Link> to start.</>
            ) : (
              <>Pending requests will show in <Link to="/agent/withdrawals" className="text-amber-300 hover:underline">Pending Withdrawals</Link>.</>
            )}
          </div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid={`recent-${tab}-list`}>
          {items.map((it) =>
            tab === "deposits"
              ? <RecentDepositRow key={it.id} d={it} />
              : <RecentWithdrawRow key={it.id} w={it} />
          )}
        </ul>
      )}
    </>
  );
}

function RecentTabBtn({ active, onClick, Icon, tone, count, children, testid }) {
  const tones = {
    emerald: { activeBg: "bg-emerald-500/15", activeText: "text-emerald-300", ring: "rgba(52,211,153,0.3)" },
    violet:  { activeBg: "bg-violet-500/15",  activeText: "text-violet-300",  ring: "rgba(167,139,250,0.3)" },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 sm:px-4 h-8 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 transition ${
        active ? `${t.activeBg} ${t.activeText}` : "text-white/55 hover:text-white"
      }`}
      style={active ? { boxShadow: `inset 0 0 0 1px ${t.ring}` } : undefined}
    >
      <Icon size={12} />
      {children}
      <span className={`ml-0.5 text-[10px] opacity-70 ${active ? "" : "text-white/40"}`}>({count})</span>
    </button>
  );
}

function RecentDepositRow({ d }) {
  return (
    <li className="group flex items-center justify-between rounded-xl px-3 py-2.5 border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] hover:border-emerald-500/30 transition gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="h-9 w-9 rounded-lg grid place-items-center font-black text-[12px] flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, rgba(52,211,153,0.18), rgba(16,185,129,0.06))",
            color: "#34d399",
            border: "1px solid rgba(52,211,153,0.3)",
          }}
        >
          {(d.username || "U").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-white truncate">@{d.username}</div>
          <div className="text-[10.5px] text-white/40 truncate">{new Date(d.created_at).toLocaleString()}</div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-mono text-[14px] font-black text-emerald-300 tabular-nums">{fmt(d.amount)}</div>
        <div className="text-[10px] text-emerald-400/60 font-bold uppercase tracking-wider">Sent</div>
      </div>
    </li>
  );
}

function RecentWithdrawRow({ w }) {
  const status = (w.status || "pending").toLowerCase();
  const statusMeta = {
    pending:  { label: "Pending",  color: "text-amber-300/85",   border: "rgba(251,191,36,0.30)" },
    approved: { label: "Approved", color: "text-emerald-300/85", border: "rgba(52,211,153,0.30)" },
    rejected: { label: "Rejected", color: "text-rose-300/85",    border: "rgba(244,63,94,0.30)" },
  }[status] || { label: status.toUpperCase(), color: "text-white/60", border: "rgba(255,255,255,0.08)" };

  const amountColor =
    status === "approved" ? "text-emerald-300"
    : status === "rejected" ? "text-rose-300"
    : "text-amber-300";

  return (
    <li
      className="group flex items-center justify-between rounded-xl px-3 py-2.5 border bg-white/[0.02] hover:bg-white/[0.04] transition gap-3"
      style={{ borderColor: statusMeta.border }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="h-9 w-9 rounded-lg grid place-items-center font-black text-[12px] flex-shrink-0"
          style={{
            background: "linear-gradient(135deg,#fbbf24,#f59e0b)",
            color: "#1a1208",
          }}
        >
          {(w.username || "U").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[12.5px] font-bold text-white truncate">@{w.username}</span>
            {w.code && (
              <span className="font-mono text-[9.5px] text-amber-300/85 bg-amber-500/10 border border-amber-500/25 px-1.5 py-0.5 rounded tabular-nums">
                {w.code}
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-white/40 truncate">{new Date(w.created_at).toLocaleString()}</div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className={`font-mono text-[14px] font-black tabular-nums ${amountColor}`}>{fmt(w.amount)}</div>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${statusMeta.color}`}>{statusMeta.label}</div>
      </div>
    </li>
  );
}
